import PropTypes from 'prop-types';
import { createContext, useEffect, useMemo } from 'react';

// project-imports
import useLocalStorage from 'hooks/useLocalStorage';
import config from 'config';

// initial state
const initialState = {
  ...config,
  onChangeLocalization: () => {},
  onChangeMenuOrientation: () => {},
  onChangeDirection: () => {},
  onChangeContainer: () => {},
  onChangeCaption: () => {},
  onChangeSideTheme: () => {},
  onChangeThemePreset: () => {},
  onChangeMenuIcon: () => {},
  onChangeMode: () => {},
  onReset: () => {}
};

const ConfigContext = createContext(initialState);

function ConfigProvider({ children }) {
  const [config, setConfig] = useLocalStorage('datta-able-react-ts-config', initialState);

  useEffect(() => {
    const width = window.innerWidth;
    if (width < 1025 && config.menuOrientation !== 'vertical') {
      setConfig((prevConfig) => ({
        ...prevConfig,
        menuOrientation: 'vertical'
      }));
    }
  });

  const onReset = () => {
    setConfig(initialState);
  };

  const onChangeLocalization = (lang) => {
    setConfig({
      ...config,
      i18n: lang
    });
  };

  const onChangeMenuOrientation = (layout) => {
    if (window.innerWidth >= 1025) {
      setConfig({
        ...config,
        menuOrientation: layout
      });
    }
  };

  const onChangeMode = (selectedMode) => {
    setConfig({
      ...config,
      mode: selectedMode
    });
  };

  const onChangeCaption = (caption) => {
    setConfig({
      ...config,
      caption: caption
    });
  };

  const onChangeSideTheme = (sidebarTheme) => {
    setConfig({
      ...config,
      sidebarTheme: sidebarTheme
    });
  };

  const onChangeDirection = (direction) => {
    setConfig({
      ...config,
      themeDirection: direction
    });
  };

  const onChangeContainer = (container) => {
    setConfig({
      ...config,
      container: container
    });
  };

  const onChangeThemePreset = (key, value) => {
    setConfig({
      ...config,
      [key]: value
    });
  };

  const onChangeMenuIcon = (key, value) => {
    setConfig({
      ...config,
      [key]: value
    });
  };

  // Memoize context value to prevent unnecessary re-renders
  // Only recreate when config actually changes
  const contextValue = useMemo(
    () => ({
      ...config,
      onChangeLocalization,
      onChangeMenuOrientation,
      onChangeMode,
      onChangeDirection,
      onChangeContainer,
      onChangeCaption,
      onChangeSideTheme,
      onChangeThemePreset,
      onChangeMenuIcon,
      onReset
    }),
    [config]
  );

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
}

export { ConfigProvider, ConfigContext };

ConfigProvider.propTypes = { children: PropTypes.node };
